import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/buyers/register")({
	component: RegisterPage,
});

function RegisterPage() {
	const navigate = useNavigate();
	const { register } = useAuth();
	const [isLoading, setIsLoading] = useState(false);
	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		email: "",
		password: "",
		confirmPassword: "",
		orgName: "",
	});
	const [errors, setErrors] = useState<Record<string, string>>({});

	const validateForm = () => {
		const newErrors: Record<string, string> = {};

		if (!formData.firstName.trim()) {
			newErrors.firstName = "First name is required";
		}
		if (!formData.lastName.trim()) {
			newErrors.lastName = "Last name is required";
		}
		if (!formData.email.trim()) {
			newErrors.email = "Email is required";
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
			newErrors.email = "Please enter a valid email address";
		}
		if (!formData.orgName.trim()) {
			newErrors.orgName = "Organization name is required";
		}
		if (!formData.password) {
			newErrors.password = "Password is required";
		} else if (formData.password.length < 8) {
			newErrors.password = "Password must be at least 8 characters";
		} else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
			newErrors.password =
				"Password must contain uppercase, lowercase, and a number";
		}
		if (formData.password !== formData.confirmPassword) {
			newErrors.confirmPassword = "Passwords do not match";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm()) return;

		setIsLoading(true);
		try {
			const result = await register({
				firstName: formData.firstName,
				lastName: formData.lastName,
				email: formData.email,
				password: formData.password,
				orgName: formData.orgName,
			});

			if (result.success) {
				if (result.needsEmailConfirmation) {
					toast.success(
						"Account created! Please check your email to confirm your account.",
						{ duration: 6000 },
					);
					// Clear the form
					setFormData({
						firstName: "",
						lastName: "",
						email: "",
						password: "",
						confirmPassword: "",
						orgName: "",
					});
				} else {
					toast.success("Account created successfully!");
					navigate({ to: "/solicitations" });
				}
			} else {
				toast.error("Failed to create account");
			}
		} catch (error) {
			const message =
				error instanceof Error ? error.message : "Failed to create account";
			toast.error(message);
		} finally {
			setIsLoading(false);
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
		if (errors[name]) {
			setErrors((prev) => ({ ...prev, [name]: "" }));
		}
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
			<Card className="w-full max-w-md">
				<CardHeader className="space-y-1">
					<CardTitle className="text-2xl font-bold text-center">
						Create your account
					</CardTitle>
					<CardDescription className="text-center">
						Sign up for Bidscope Purchasing
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleSubmit} className="space-y-4" noValidate>
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label htmlFor="firstName">First Name</Label>
								<Input
									id="firstName"
									name="firstName"
									placeholder="John"
									value={formData.firstName}
									onChange={handleChange}
									aria-invalid={!!errors.firstName}
								/>
								{errors.firstName && (
									<p className="text-sm text-red-500">{errors.firstName}</p>
								)}
							</div>
							<div className="space-y-2">
								<Label htmlFor="lastName">Last Name</Label>
								<Input
									id="lastName"
									name="lastName"
									placeholder="Doe"
									value={formData.lastName}
									onChange={handleChange}
									aria-invalid={!!errors.lastName}
								/>
								{errors.lastName && (
									<p className="text-sm text-red-500">{errors.lastName}</p>
								)}
							</div>
						</div>

						<div className="space-y-2">
							<Label htmlFor="email">Email</Label>
							<Input
								id="email"
								name="email"
								type="email"
								placeholder="john.doe@example.gov"
								value={formData.email}
								onChange={handleChange}
								aria-invalid={!!errors.email}
							/>
							{errors.email && (
								<p className="text-sm text-red-500">{errors.email}</p>
							)}
						</div>

						<div className="space-y-2">
							<Label htmlFor="orgName">Organization Name</Label>
							<Input
								id="orgName"
								name="orgName"
								placeholder="City of Springfield"
								value={formData.orgName}
								onChange={handleChange}
								aria-invalid={!!errors.orgName}
							/>
							{errors.orgName && (
								<p className="text-sm text-red-500">{errors.orgName}</p>
							)}
						</div>

						<div className="space-y-2">
							<Label htmlFor="password">Password</Label>
							<Input
								id="password"
								name="password"
								type="password"
								placeholder="Enter your password"
								value={formData.password}
								onChange={handleChange}
								aria-invalid={!!errors.password}
							/>
							{errors.password && (
								<p className="text-sm text-red-500">{errors.password}</p>
							)}
						</div>

						<div className="space-y-2">
							<Label htmlFor="confirmPassword">Confirm Password</Label>
							<Input
								id="confirmPassword"
								name="confirmPassword"
								type="password"
								placeholder="Confirm your password"
								value={formData.confirmPassword}
								onChange={handleChange}
								aria-invalid={!!errors.confirmPassword}
							/>
							{errors.confirmPassword && (
								<p className="text-sm text-red-500">{errors.confirmPassword}</p>
							)}
						</div>

						<Button type="submit" className="w-full" disabled={isLoading}>
							{isLoading ? "Creating account..." : "Create account"}
						</Button>
					</form>

					<div className="mt-4 text-center text-sm">
						Already have an account?{" "}
						<a
							href="/buyers/login"
							className="text-primary underline underline-offset-4 hover:text-primary/80"
						>
							Sign in
						</a>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
